import React, { memo } from 'react';
import { Cell, DatePicker, Row, Select } from '@dexma/ui-components';
import { StyledSelectCell, StyledWidgetSelector } from 'adapter/in/common/components/Selector/style';

import gtmIds from 'adapter/in/dashboards/config/gtm';
import { SelectorOption, SelectorValueOptions } from 'adapter/in/common/form/types';
import { FooterInputText } from 'adapter/in/common/components/Text';
import i18n from 'asset/translation';
import { normalizeDateFormat } from 'adapter/in/common/utils/DateUtils';
import dayjs, { Dayjs } from 'dayjs';

type CommonProps = {
    label: string;
    name: string;
    placeholder?: string;
    isLoading: boolean;
    error: boolean;
    maxSelectionLength: number;
    minSelectionLength: number;
    onChange: (selectedValue: string | string[]) => void;
    onClear?: (onChangeCallback: (e: string | string[]) => void) => void;
    handleButtonSelectAll?: (values: string[]) => void;
    handleClearAll?: () => void;
    disabled?: boolean;
    errorMessage?: string;
    footer?: string;
    style?: React.CSSProperties;
    dateFormat?: string;
};

type DatePickerProps = CommonProps & {
    asDatePicker: true;
    options?: undefined;
    value?: string;
};

type SelectProps = CommonProps & {
    asDatePicker?: false;
    options: SelectorOption[] | undefined;
    value: SelectorValueOptions | undefined;
};

export type WidgetSelectorType = DatePickerProps | SelectProps;

export const mapValues = (values?: SelectorOption[]): string[] => (values ? values.map((value: SelectorOption) => value.value) : []);

const FormSelector = memo((props: WidgetSelectorType) => {
    const {
        label,
        onChange,
        onClear,
        handleButtonSelectAll,
        handleClearAll,
        disabled,
        placeholder,
        isLoading,
        error,
        errorMessage,
        maxSelectionLength,
        minSelectionLength,
        name,
        footer,
        style,
        dateFormat,
    } = props;

    const parsedValues = !props.asDatePicker ? mapValues(props.value as SelectorOption[]) : undefined;

    const isMultiple = maxSelectionLength > 1;
    const allowClear = minSelectionLength === 0 || isMultiple;

    return (
        <StyledWidgetSelector $error={!isLoading && error} style={style}>
            <Row>
                <Cell className='widget-type-block'>
                    <span>{label}</span>
                </Cell>
            </Row>

            <Row>
                <StyledSelectCell>
                    {props.asDatePicker ? (
                        <DatePicker
                            style={{ width: '50%' }}
                            type='date'
                            picker='week'
                            label='Select week'
                            format={normalizeDateFormat(dateFormat)}
                            value={props.value ? dayjs(props.value) : null}
                            onChange={(dateAsDayjs: Dayjs | null) => {
                                onChange(dateAsDayjs ? dateAsDayjs.toISOString() : '');
                            }}
                            calendarIconAriaLabel={i18n.t('common.ariaLabel.datePicker.calendarIcon')}
                            prevPageIconAriaLabel={i18n.t('common.ariaLabel.datePicker.prevPageIcon')}
                            nextPageIconAriaLabel={i18n.t('common.ariaLabel.datePicker.nextPageIcon')}
                            superPrevPageIconAriaLabel={i18n.t('common.ariaLabel.datePicker.superPrevPageIcon')}
                            superNextPageIconAriaLabel={i18n.t('common.ariaLabel.datePicker.superNextPageIcon')}
                            disabled={disabled}
                        />
                    ) : (
                        <Select
                            className={`select_${name}`}
                            data-testid={name}
                            dataId={`${gtmIds.addWidget.sidePanel.form.select}.${name}`}
                            isLoading={isLoading}
                            placeholder={placeholder}
                            options={props.options}
                            onChange={onChange}
                            onClear={onClear as any}
                            mode={isMultiple ? 'multiple' : 'single'}
                            handleButtonSelectAll={handleButtonSelectAll}
                            handleClearAll={handleClearAll}
                            defaultValues={parsedValues}
                            value={parsedValues}
                            pageSize={maxSelectionLength}
                            allowClear={allowClear}
                            showOptionsAriaLabel={i18n.t('common.ariaLabel.showSelectOptions')}
                            hideOptionsAriaLabel={i18n.t('common.ariaLabel.hideSelectOptions')}
                            clearAllOptionsAriaLabel={i18n.t('common.ariaLabel.clearAllOptions')}
                            deleteOptionSelectedAriaLabel={i18n.t('common.ariaLabel.deleteOptionSelected')}
                            disabled={disabled}
                        />
                    )}

                    {!isLoading && error && <div className='error'>{errorMessage}</div>}
                </StyledSelectCell>
            </Row>

            {footer && <FooterInputText text={footer} />}
        </StyledWidgetSelector>
    );
});

export default FormSelector;
